import type { FC } from 'react';
import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TableStyles from '../documents/Table.module.css';
import { Link, useParams } from 'react-router';
import { normalizeKey } from './matDiscoveryTableHelpers';

interface Props {
  columns: string[];
  requiredSet: Set<string>;
  readOnlySet: Set<string>;
  requiredErrors: Set<string>;
  draft: Record<string, any>;
  setField: (key: string, value: any) => void;
  blockEditing: boolean;
  existingMat?: any;
  current?: any;
  copyFromRow: (source: Record<string, any>) => void;
  loadingRow: boolean;
  coerce: (v: any) => string;
  getOptions: (key: string) => string[];
}

const MATDiscoveryTableHorizontal: FC<Props> = ({
  columns,
  requiredSet,
  readOnlySet,
  requiredErrors,
  draft,
  setField,
  blockEditing,
  existingMat,
  current,
  copyFromRow,
  loadingRow,
  coerce,
  getOptions,
}) => {
  const { dpid } = useParams<{ dpid: string }>();

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <Paper className={TableStyles.documentsTablePaper}>
        <Table className={TableStyles.documentsTable}>
        <TableHead className={TableStyles.documentsTableHead}>
          <TableRow>
            <TableCell className={TableStyles.documentsTableHeadCell} style={{ minWidth: 50, padding: '8px' }}>
              Copy
            </TableCell>
            <TableCell className={TableStyles.documentsTableHeadCell} style={{ minWidth: 220, padding: '8px' }}>
              Source
            </TableCell>
            {columns.map((col) => (
              <TableCell
                key={col}
                className={TableStyles.documentsTableHeadCell}
                style={{ minWidth: 220, padding: '8px' }}
              >
                <span style={{ fontSize: '0.85rem' }}>{col}</span>
                {requiredSet.has(col) && !readOnlySet.has(normalizeKey(col)) && (
                  <span style={{ color: '#d32f2f', marginLeft: 4 }} title="Required">
                    *
                  </span>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingRow ? (
            <TableRow>
              <TableCell colSpan={Math.max(columns.length + 2, 1)}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : (
            <>
              <TableRow>
                <TableCell
                  colSpan={columns.length + 2}
                  style={{ background: 'transparent', padding: '4px 12px 0' }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }}
                  >
                    Build Row
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow
                className={TableStyles.documentsTableRow}
                style={{ backgroundColor: '#F5F7FA' }}
              >
                <TableCell className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}></TableCell>
                <TableCell className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}></TableCell>
                {columns.map((col) => {
                  const isRO = readOnlySet.has(normalizeKey(col));
                  const isReq = requiredSet.has(col) && !isRO;
                  const showError =
                    isReq &&
                    requiredErrors.has(col) &&
                    (draft[col] == null || String(draft[col]).trim() === '');
                  return (
                    <TableCell key={col} className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}>
                      {isRO ? (
                        <Typography
                          variant="body2"
                          title="Read only"
                          sx={{
                            opacity: 0.75,
                            fontSize: 13,
                            minHeight: 28,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {draft[col] ?? ''}
                        </Typography>
                      ) : (
                        <Autocomplete
                          freeSolo
                          fullWidth
                          disabled={blockEditing}
                          options={getOptions(col)}
                          value={draft[col] ?? ''}
                          onChange={(_, newValue) => setField(col, newValue ?? '')}
                          onInputChange={(_, newInputValue) => setField(col, newInputValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              hiddenLabel
                              size="small"
                              placeholder={isReq ? 'Required…' : 'Select or type custom…'}
                              fullWidth
                              error={showError}
                              helperText={showError ? 'Required' : undefined}
                              inputProps={{
                                ...params.inputProps,
                                'data-cy': `input-build-row-${col}`,
                              }}
                            />
                          )}
                          sx={{
                            width: '100%',
                            minWidth: '200px',
                            '& .MuiInputBase-root': {
                              width: '100%',
                              minHeight: '32px',
                              padding: '2px 8px'
                            },
                            '& .MuiAutocomplete-input': {
                              fontSize: 13,
                              padding: '2px 4px !important',
                              minWidth: '180px !important'
                            },
                            '& .MuiInputBase-input': {
                              padding: '4px !important'
                            },
                            ...(showError
                              ? {
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d32f2f',
                                  },
                                }
                              : {}),
                          }}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>

              {existingMat ? (
                <>
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 2}
                      style={{ background: 'transparent', padding: '6px 12px 0' }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }}
                      >
                        Existing MAT Row
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    className={TableStyles.documentsTableRow}
                    style={{ backgroundColor: '#FFFBEA', borderTop: '2px solid #E0E0E0' }}
                  >
                    <TableCell className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}>
                      <Tooltip title="Apply All To Build Row">
                        <IconButton onClick={() => copyFromRow(existingMat as any)} size="small">
                          <CopyAllIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}>
                      {dpid && existingMat?.MatKey ? (
                        <Link
                          to={`/data-pools/${dpid}/mat?MatKey=${existingMat.MatKey}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            View MAT Entry
                          </Typography>
                        </Link>
                      ) : (
                        <span style={{ opacity: 0.6 }}>—</span>
                      )}
                    </TableCell>
                    {columns.map((col) => {
                      const raw = (existingMat as any)[col];
                      const text = String(raw ?? '');
                      const isSame =
                        text !== '' && String(draft[col] ?? '') === String(coerce(raw));
                      return (
                        <TableCell key={col} className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 4,
                              opacity: isSame ? 0.5 : 1,
                            }}
                          >
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '13px',
                              }}
                            >
                              {text}
                            </span>
                            {text !== '' && (
                              <Tooltip
                                title={
                                  readOnlySet.has(normalizeKey(col))
                                    ? 'Read only'
                                    : isSame
                                      ? 'Already in Build row'
                                      : 'Use in Build row'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => setField(col, coerce(raw))}
                                    sx={{ p: 0.5 }}
                                    disabled={isSame || readOnlySet.has(normalizeKey(col)) || blockEditing}
                                  >
                                    <ContentCopyIcon fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </>
              ) : null}

              {(existingMat ? true : (current?.discovered_rows?.length || 0) > 0) && (
                <>
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 2}
                      style={{ background: 'transparent', padding: '6px 12px 0' }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }}
                      >
                        Discovered Rows
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {current?.discovered_rows?.map((row: any, idx: number) => (
                    <TableRow key={idx} className={TableStyles.documentsTableRow}>
                      <TableCell className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}>
                        <Tooltip title="Apply All To Build Row">
                          <IconButton
                            onClick={() => {
                              // Include DataSourceStatus from discovery item level
                              const rowWithStatus = {
                                ...row.data,
                                ...(current?.data_source_status && { DataSourceStatus: current.data_source_status })
                              };
                              copyFromRow(rowWithStatus);
                            }}
                            size="small"
                          >
                            <CopyAllIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}>
                        {row?.data_pool && row?.source_extraction_batch ? (
                          <Link
                            to={`/data-pools/${row.data_pool}/review/${row.source_extraction_batch}/extraction`}
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              variant="body2"
                              color="primary"
                              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              View Source
                            </Typography>
                          </Link>
                        ) : (
                          <span style={{ opacity: 0.6 }}>—</span>
                        )}
                      </TableCell>
                      {columns.map((col) => {
                        // DataSourceStatus is at the discovery item level, not in row.data
                        const isDataSourceStatus = normalizeKey(col) === normalizeKey('DataSourceStatus');
                        const raw = isDataSourceStatus ? current?.data_source_status : (row?.data ?? {})[col];
                        const text = String(raw ?? '');
                        const isSame =
                          text !== '' && String(draft[col] ?? '') === String(coerce(raw));
                        return (
                          <TableCell key={col} className={TableStyles.documentsTableRowCell} style={{ padding: '6px 8px' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 4,
                                opacity: isSame ? 0.5 : 1,
                              }}
                            >
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '13px',
                                }}
                              >
                                {text}
                              </span>
                              {text !== '' && (
                                <Tooltip
                                  title={
                                    readOnlySet.has(normalizeKey(col))
                                      ? 'Read only'
                                      : isSame
                                        ? 'Already in Build row'
                                        : 'Use in Build row'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => setField(col, coerce(raw))}
                                      sx={{ p: 0.5 }}
                                      disabled={isSame || readOnlySet.has(normalizeKey(col)) || blockEditing}
                                      data-cy={`btn-copy-discovered-${col}-${idx}`}
                                    >
                                      <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </Paper>
    </div>
  );
};

export default MATDiscoveryTableHorizontal;
